import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, PencilSimple } from "@phosphor-icons/react";
import MarketingLayout from "@/components/MarketingLayout";
import SEO from "@/components/SEO";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

function fmtDate(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" }); }
  catch { return iso; }
}

export default function Blog() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/blog/posts", { params: { include_drafts: isAdmin ? true : false } })
      .then(({ data }) => setPosts(data))
      .finally(() => setLoading(false));
  }, [isAdmin]);

  return (
    <MarketingLayout>
      <SEO
        title="Blog"
        description="Tidy thoughts on home, habits, and the things we keep. The Nestled blog — short essays from a small team building a cozy home-inventory app."
        path="/blog"
      />
      <section className="max-w-4xl mx-auto px-6 md:px-10 pt-16 md:pt-20 pb-12">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <span className="text-xs font-bold uppercase tracking-[0.18em] text-terracotta-600">Notes from the garage</span>
            <h1 className="mt-3 font-heading text-4xl sm:text-5xl lg:text-6xl text-stoneink-900 font-semibold tracking-tight">The Nestled blog.</h1>
            <p className="mt-4 text-stoneink-700 text-lg">Tidy thoughts on home, habits, and the things we keep.</p>
          </div>
          {isAdmin && (
            <Link to="/admin/blog" data-testid="blog-admin-link"
              className="h-11 px-5 rounded-xl bg-stoneink-900 hover:bg-stoneink-700 text-white font-medium flex items-center gap-2">
              <PencilSimple weight="bold" size={16} /> Manage posts
            </Link>
          )}
        </div>
      </section>

      <section className="max-w-4xl mx-auto px-6 md:px-10 pb-24">
        {loading ? (
          <div className="text-stoneink-500" data-testid="blog-loading">Loading…</div>
        ) : posts.length === 0 ? (
          <div className="rounded-3xl border border-dashed border-sand-300 bg-white p-10 text-center" data-testid="blog-empty">
            <div className="text-stoneink-700 font-medium">No posts yet.</div>
            <div className="text-stoneink-500 text-sm mt-1">Come back soon — we&apos;re writing.</div>
          </div>
        ) : (
          <div className="space-y-5" data-testid="blog-list">
            {posts.map((p, i) => (
              <motion.div key={p.id}
                initial={{ opacity: 0, y: 8 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.04 }}>
                <Link to={`/blog/${p.slug}`} data-testid={`blog-post-${p.slug}`}
                  className="block rounded-3xl border border-sand-300 bg-white shadow-ambient hover:shadow-hover hover:-translate-y-0.5 transition-all overflow-hidden">
                  <div className="grid grid-cols-1 sm:grid-cols-[200px_1fr]">
                    {p.cover_image ? (
                      <img src={p.cover_image} alt="" className="h-full w-full object-cover sm:aspect-[3/2]" />
                    ) : (
                      <div className="bg-gradient-to-br from-terracotta-50 to-ochre-400/20 grid place-items-center text-terracotta-600 font-heading text-4xl font-semibold p-6">
                        {p.title?.charAt(0)?.toUpperCase() || "N"}
                      </div>
                    )}
                    <div className="p-6 sm:p-7">
                      <div className="flex items-center gap-2 text-xs text-stoneink-500">
                        <span>{fmtDate(p.published_at)}</span>
                        {!p.published && <span className="px-2 py-0.5 rounded-full bg-ochre-400/30 text-ochre-600 font-medium">draft</span>}
                      </div>
                      <h2 className="mt-2 font-heading text-2xl text-stoneink-900 font-medium tracking-tight">{p.title}</h2>
                      {p.excerpt && <p className="mt-2 text-stoneink-700 text-sm leading-relaxed line-clamp-2">{p.excerpt}</p>}
                      <div className="mt-4 flex items-center gap-1 text-terracotta-600 text-sm font-medium">Read <ArrowRight weight="bold" size={14} /></div>
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        )}
      </section>
    </MarketingLayout>
  );
}
